<template>
  <!-- [#864] El perfil de CRM: la única llave del CRM, que se da y se quita aquí, en la ficha. Va en
       su propia tarjeta al final del formulario para que nada de lo que ya había cambie de sitio.
       Se guarda con el botón «Guardar» de la ficha, como el resto de sus campos. -->
  <f7-card class="padding-vertical-half padding-right tarjeta-perfil-crm">
    <f7-card-header>{{ $t('empleados.perfilCrm.titulo') }}</f7-card-header>
    <f7-card-content>
      <f7-block v-if="cargaFallida" strong class="no-margin">
        <p>
          {{ $t('empleados.perfilCrm.sinCargar') }}
          <f7-link class="margin-left-half" @click="cargar()">{{ $t('empleados.perfilCrm.reintentar') }}</f7-link>
        </p>
      </f7-block>
      <template v-else-if="opciones">
        <f7-row no-gap class="list list-form no-hairlines">
          <f7-col width="100" medium="50">
            <ul>
              <r-input
                outline
                floating-label
                :label="$t('empleados.perfilCrm.label')"
                :form="form"
                type="select"
                name="perfilCrm"
                :readonly="readonly"
              >
                <!-- Las que no se pueden elegir se ven, apagadas, y el porqué va justo debajo.
                     «Sin acceso» va con valor vacío y no `null`: con `null` el navegador toma el
                     texto de la opción como valor, y `r-input` convierte el vacío en `null`, que
                     es lo que la API entiende como quitar el perfil. -->
                <option
                  v-for="o in opciones"
                  :key="o.clave"
                  :value="o.valor || ''"
                  :disabled="!o.permitida"
                >{{ etiqueta(o.valor) }}{{ o.permitida ? '' : ' · ' + $t('empleados.perfilCrm.noDisponible') }}</option>
              </r-input>
            </ul>
          </f7-col>
          <f7-col width="100" medium="50">
            <p class="perfil-crm-que-es">{{ $t('empleados.perfilCrm.queEs') }}</p>
          </f7-col>
        </f7-row>
        <!-- §9: por qué no, quién puede y dónde, junto al control y solo mientras se edita. -->
        <ul v-if="!readonly && motivos.length" class="perfil-crm-motivos">
          <li v-for="m in motivos" :key="m">{{ m }}</li>
        </ul>
        <div class="perfil-crm-historial">
          <div class="perfil-crm-historial-titulo">{{ $t('empleados.perfilCrm.historial') }}</div>
          <p v-if="!empleadoId" class="perfil-crm-vacio">{{ $t('empleados.perfilCrm.historialAlta') }}</p>
          <p v-else-if="!historial.length" class="perfil-crm-vacio">{{ $t('empleados.perfilCrm.historialVacio') }}</p>
          <table v-else class="perfil-crm-tabla">
            <thead>
              <tr>
                <th>{{ $t('empleados.perfilCrm.cuando') }}</th>
                <th>{{ $t('empleados.perfilCrm.cambio') }}</th>
                <th>{{ $t('empleados.perfilCrm.quien') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(h, i) in historial" :key="i">
                <td>{{ fecha(h.fecha) }}</td>
                <td>{{ etiqueta(h.anterior) }} → {{ etiqueta(h.nuevo) }}<span v-if="origen(h)" class="perfil-crm-origen"> · {{ origen(h) }}</span></td>
                <td>{{ h.autor }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </f7-card-content>
  </f7-card>
</template>
<style scoped>
  .perfil-crm-que-es,
  .perfil-crm-vacio {
    font-size: 13px;
    opacity: 0.85;
    margin: 8px 16px;
  }
  .perfil-crm-motivos {
    list-style: none;
    padding: 0 16px;
    margin: 0 0 8px;
    font-size: 13px;
    opacity: 0.85;
  }
  .perfil-crm-motivos li + li {
    margin-top: 4px;
  }
  .perfil-crm-historial {
    padding: 0 16px;
  }
  .perfil-crm-historial-titulo {
    font-weight: bold;
    margin: 8px 0 4px;
  }
  .perfil-crm-tabla {
    border-collapse: collapse;
    font-size: 13px;
  }
  .perfil-crm-tabla th,
  .perfil-crm-tabla td {
    text-align: left;
    padding: 2px 16px 2px 0;
  }
  .perfil-crm-origen {
    opacity: 0.7;
  }
</style>
<script>
import rInput from "./../../components/rInput.vue";
import Empleado from "./../../js/db/Empleado.js";

const ORIGENES = {
  MIGRACION: 'migracion', ALTA_FICHA: 'altaFicha', BAJA_FICHA: 'bajaFicha', BAJA_SAP: 'bajaSap',
  CAMBIO_USUARIO: 'cambioUsuario'
};

export default {
  components: { rInput },
  props: {
    /** El `rform` de la ficha: el perfil se guarda con ella. */
    form: { type: Object, required: true },
    /** El id ya guardado; en el alta, `null` hasta que se guarda. */
    empleadoId: { type: String, default: null },
    readonly: { type: Boolean, default: false }
  },
  data: function() {
    return {
      opciones: null,
      historial: [],
      cargaFallida: false
    };
  },
  computed: {
    /**
     * Los motivos de las opciones apagadas, sin repetir: Dirección y Administrador comparten el
     * suyo, y decirlo dos veces seguidas no explica nada más.
     */
    motivos: function() {
      const vistos = [];
      (this.opciones || []).forEach(function(o) {
        if (!o.permitida && o.motivo && vistos.indexOf(o.motivo) < 0) {
          vistos.push(o.motivo);
        }
      });
      return vistos;
    }
  },
  watch: {
    // En el alta el id llega al guardar: entonces ya hay historial que pedir.
    empleadoId: function() {
      this.cargar();
    }
  },
  methods: {
    etiqueta: function(valor) {
      return this.$t('empleados.perfilCrm.valores.' + (valor || 'SIN_ACCESO'));
    },
    origen: function(apunte) {
      const clave = ORIGENES[apunte.origen];
      // El cambio de usuario dice de qué cuenta venía el perfil: el CRM da el acceso por usuario.
      return clave ? this.$t('empleados.perfilCrm.origenes.' + clave, { antes: apunte.usernameAnterior || '' }) : '';
    },
    fecha: function(valor) {
      if (!valor) return '';
      const d = new Date(valor);
      return isNaN(d.getTime()) ? String(valor) : d.toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    },
    // La API omite los nulos: una opción sin `valor` es «Sin acceso».
    normalizar: function(opciones) {
      return (opciones || []).map(function(o) {
        const valor = o.valor || null;
        return { valor: valor, clave: valor || 'SIN_ACCESO', permitida: !!o.permitida, motivo: o.motivo || null };
      });
    },
    /** La recarga la pide también la ficha al guardar: el historial acaba de cambiar. */
    recargar: function() {
      this.cargar();
    },
    cargar: function() {
      const self = this;
      self.cargaFallida = false;
      const peticion = self.empleadoId
        ? Empleado.perfilCrm(self.empleadoId).then(function(res) {
            self.historial = (res && res.historial) || [];
            return res && res.opciones;
          })
        : Empleado.opcionesPerfilCrm();
      peticion.then(function(opciones) {
        self.opciones = self.normalizar(opciones);
      }).catch(function() {
        self.cargaFallida = true;
      });
    }
  },
  created: function() {
    this.cargar();
  }
};
</script>
